import React, { useState, useEffect } from 'react'
import { RouteComponentProps, NavigateFn } from '@reach/router'
import { Checkbox, H4, Classes, EditableText } from '@blueprintjs/core'
import {
  IconButton,
  Confirmation,
  Avatar,
  Flex,
  RoleSelect,
  EmployeeWithDetailsPopover,
  Toaster,
  Dialog,
} from '@ui'
import useEmployeeDetails from './hooks/useEmployeeDetails'
import EmployeeSpaceTag from './EmployeeSpaceTag'
import ManagedLocationsList from './ManagedLocationsList'
import ManagedDepartmentsList from './ManagedDepartmentsList'
import EmployeeForm from './EmployeeForm'
import { employeeDetailsToFormEmployee } from './utils'
import api, { Role, IEmployeeDetails } from '@api'
// import moment from 'moment'
import track from '@track'
import Event from '@track/Event'
import { Permissions, PermissionsType, IPermissionsOnEmployee } from '@permissions'
import { ApolloError } from 'apollo-boost'
import { ReloadFunc } from '@ui/ModalLayer'
import ManagedEmployeesList from './ManagedEmployeesList'
import css from './employeeManagement.module.scss'
import { get } from 'lodash'
import { DateUtil } from '@utils'

export default function EmployeeDetailsDialog({
  employeeId,
  navigate,
  reload,
}: RouteComponentProps<{
  employeeId: string
}> & {
  reload?: ReloadFunc
}) {
  const {
    employee,
    updateRole,
    updateBookingManagerManageAll,
    toggleRemote,
    toggleNoDeskNeeded,
    update,
    updateNote,
  } = useEmployeeDetails(employeeId)
  const [editMode, setEditMode] = useState(false)

  const [dirty, setDirty] = useState(false)
  const toolbarProps: DialogToolbarProps = { employee }
  const [bookingManagerSetting, setBookingManagerSetting] = useState({})
  const onClose = () => navigate && navigate(window.location.pathname)
  useEffect(() => {
    if (employee?.role === Role.BOOKINGS_MANAGER) {
      setBookingManagerSetting(employee?.user?.bookingManagerSetting)
    }
  }, [employee?.id])
  return (
    <Dialog
      isOpen
      style={{ width: 900, position: 'relative' }}
      usePortal
      canEscapeKeyClose={false}
      canOutsideClickClose={!editMode}
      transitionDuration={200}
      isCloseButtonShown={false}
      title={editMode ? 'Update employee' : ''}
      onClose={onClose}
      toolbar={
        employee ? (
          <Permissions type={PermissionsType.EMPLOYEE} on={employee}>
            {(permissions) => {
              const { canEdit, canDelete, canChangeRole, canToggleRemote, canEditNote } =
                permissions as IPermissionsOnEmployee
              if (!dirty) {
                toolbarProps.onClose = onClose
              }

              // Only show Edit|Delete buttons if not in edit mode
              if (!editMode) {
                if (canEdit) {
                  toolbarProps.onEdit = () => {
                    setEditMode(true)
                    track.employees({
                      eventName: Event.EDIT_RECORD_DIALOG,
                    })
                  }
                }

                if (canDelete) {
                  toolbarProps.onDelete = () => {
                    if (employee) {
                      api.employee
                        .remove(employee.id)
                        .then(() => {
                          navigate && navigate('/employees')
                          reload?.()
                        })
                        .then(() => Toaster.success({ message: 'Employee removed' }))
                        .catch((error: ApolloError) =>
                          Toaster.fail({
                            error,
                          })
                        )

                      track.employees({
                        eventName: Event.DELETE_RECORD_DIALOG,
                      })
                    }
                  }
                }
              }
              // here
              return (
                <>
                  <DialogToolbar {...toolbarProps} />
                </>
              )
            }}
          </Permissions>
        ) : (
          <>
            <DialogToolbar {...toolbarProps} />
          </>
        )
      }
      body={
        employee ? (
          <Permissions type={PermissionsType.EMPLOYEE} on={employee}>
            {(permissions) => {
              const { canChangeRole, canToggleRemote, canEditNote } =
                permissions as IPermissionsOnEmployee

              // here
              return (
                <>
                  <div className={Classes.DIALOG_BODY}>
                    {editMode ? (
                      <EmployeeForm
                        dirty={dirty}
                        setDirty={setDirty}
                        employee={employeeDetailsToFormEmployee(employee as IEmployeeDetails)}
                        onCancel={() => {
                          setEditMode(false)
                          setDirty(false)
                        }}
                        onSubmit={(updatedEmployee) => {
                          update(updatedEmployee)
                          reload && reload()
                          setEditMode(false)
                          setDirty(false)
                        }}
                      />
                    ) : (
                      <Details
                        navigate={navigate}
                        employee={employee as IEmployeeDetails}
                        bookingManagerSetting={bookingManagerSetting}
                        updateRole={
                          canChangeRole
                            ? (role: Role) => {
                                if (employee) {
                                  updateRole(role)
                                  api.employee
                                    .update({
                                      id: employee.id,
                                      role,
                                    })
                                    .then((employee: any) => {
                                      reload && reload()
                                      if (role === Role.BOOKINGS_MANAGER) {
                                        setBookingManagerSetting(
                                          employee?.user?.bookingManagerSetting
                                        )
                                      }
                                    })
                                    .then(() =>
                                      Toaster.success({
                                        message: 'Role updated',
                                      })
                                    )
                                    .catch((error: ApolloError) =>
                                      Toaster.fail({
                                        error,
                                      })
                                    )
                                }
                              }
                            : undefined
                        }
                        updateManageAllDeptAndEmp={(checked) => {
                          if (employee) {
                            updateBookingManagerManageAll(checked)
                            api.employee
                              .update({
                                id: employee.id,
                                bookingsManagerSetting: {
                                  manage_all: checked,
                                },
                              })
                              .then((employee: IEmployeeDetails) => {
                                setBookingManagerSetting(employee?.user?.bookingManagerSetting)
                              })

                              .catch((error: ApolloError) =>
                                Toaster.fail({
                                  error,
                                })
                              )
                          }
                        }}
                        updateNote={
                          canEditNote
                            ? (note: Note) => {
                                if (employee) {
                                  updateNote(note)
                                }
                              }
                            : undefined
                        }
                        toggleRemote={
                          canToggleRemote
                            ? (isRemote: boolean) => {
                                if (employee) {
                                  api.employee
                                    .update({
                                      id: employee.id,
                                      isRemote,
                                    })
                                    .then(() => {
                                      toggleRemote(isRemote)
                                      reload?.()
                                    })
                                    .then(() =>
                                      Toaster.success({
                                        message: '"Remote" status updated',
                                      })
                                    )
                                    .catch((error: ApolloError) =>
                                      Toaster.fail({
                                        error,
                                      })
                                    )
                                }
                              }
                            : undefined
                        }
                        toggleNoDeskNeeded={
                          canToggleRemote
                            ? (noDeskNeeded: boolean) => {
                                if (employee) {
                                  api.employee
                                    .update({
                                      id: employee.id,
                                      noDeskNeeded,
                                    })
                                    .then(() => {
                                      toggleNoDeskNeeded(noDeskNeeded)
                                      reload?.()
                                    })
                                    .then(() =>
                                      Toaster.success({
                                        message: '"No Desk Needed" status updated',
                                      })
                                    )
                                    .catch((error: ApolloError) =>
                                      Toaster.fail({
                                        error,
                                      })
                                    )
                                }
                              }
                            : undefined
                        }
                      />
                    )}
                  </div>
                </>
              )
            }}
          </Permissions>
        ) : (
          <>
            <DetailsSkeleton />
          </>
        )
      }
    />
  )
}

interface DialogToolbarProps {
  employee: IEmployeeDetails | null
  onClose?: () => void
  onEdit?: () => void
  onDelete?: () => void
}

function DialogToolbar({ employee, onEdit, onDelete, onClose }: DialogToolbarProps) {
  return (
    <div className="dialog-buttons">
      {employee && onEdit && (
        <IconButton
          /* eslint-disable @typescript-eslint/ban-ts-comment */
          /* @ts-ignore */
          minimal
          iconName="pen"
          tooltip="Edit"
          data-button="edit_employee"
          onClick={onEdit}
        />
      )}
      {employee && onDelete && (
        <Confirmation text="Delete this employee?" onConfirm={onDelete}>
          {/* @ts-ignore */}
          <IconButton minimal data-button="delete_employee" tooltip="Delete" iconName="trash" />
        </Confirmation>
      )}
      {onClose && (
        <IconButton
          /* @ts-ignore */
          minimal
          data-button="close_dialog"
          tooltip="Close"
          iconName="times"
          onClick={onClose}
        />
      )}
    </div>
  )
}

interface EmployeeProps {
  employee: IEmployeeDetails
  updateRole?: (role: Role) => void
  updateManageAllDeptAndEmp?: (checked: boolean) => void
  updateNote?: (note: Note) => void
  toggleRemote?: (isRemote: boolean) => void
  toggleNoDeskNeeded?: (noDeskNeeded: boolean) => void
  navigate?: NavigateFn
  bookingManagerSetting?: any
}

export type Note = string

interface NoteProps {
  note: Note
  updateNote?: (note: Note) => void
  id: string
}

function Details(props: EmployeeProps) {
  const {
    employee: { note, id },
    updateNote,
  } = props

  return (
    <>
      <section className={css.section}>
        <EmployeeCard {...props} />
      </section>
      <section className={css.section} style={{ marginTop: 14 }}>
        <EmployeeInfo {...props} />
      </section>
      <section className={css.section} style={{ marginTop: 14 }}>
        <RoleSection {...props} />
      </section>
      <section className={css.section} style={{ marginTop: 14 }}>
        <NoteSection note={note} updateNote={updateNote} id={id} />
      </section>
    </>
  )
}

function EmployeeCard({ employee, toggleRemote, toggleNoDeskNeeded }: EmployeeProps) {
  const secondary = employee.secondarySpaceAssignments.length > 0
  const neighborhoods = employee.neighborhoods.length > 0
  const emplSpace =
    get(employee, 'spaceAssignment.space') ||
    (secondary ? 'true' : null) ||
    (neighborhoods ? 'true' : null)
  const isLocated = emplSpace ? true : false
  return (
    <Flex align="center" justify="space-between">
      <div>
        <div data-field="employee_name" className={css.employee_name}>
          {employee.name}
        </div>
        <div
          data-field="employee_email"
          style={{
            fontSize: 18,
            color: 'var(--grey1)',
            marginTop: 10,
            marginBottom: 5,
          }}>
          {employee.email}
        </div>
        <Flex align="center" style={{ marginTop: 14 }}>
          <EmployeeSpaceTag employee={employee} />
          <div style={{ marginBottom: 4 }} className="flex">
            <Checkbox
              disabled={!toggleRemote || isLocated}
              checked={employee.isRemote}
              onChange={({
                currentTarget: { value, checked },
              }: React.SyntheticEvent<HTMLInputElement>) => {
                toggleRemote?.(checked)
              }}
              style={{ marginLeft: 20 }}
              label="Remote"
            />
            <Checkbox
              disabled={!toggleNoDeskNeeded || isLocated}
              checked={employee.noDeskNeeded}
              onChange={({
                currentTarget: { value, checked },
              }: React.SyntheticEvent<HTMLInputElement>) => {
                toggleNoDeskNeeded?.(checked)
              }}
              style={{ marginLeft: 10 }}
              label="No Desk Needed"
            />
          </div>
        </Flex>
      </div>
      <Avatar name={employee.name} size={Avatar.Size.LG} assets={employee.assets} />
    </Flex>
  )
}

export function showLocationsList({ role }: IEmployeeDetails) {
  const allowedToShowRoles = [Role.LOCATION_ADMIN, Role.SPACE_CAPTAIN]

  return allowedToShowRoles.some((allowed) => role === allowed)
}

export function showDepartmentsList(role: string, bookingManagerSetting?: any) {
  const allowedToShowRoles = [Role.DEPARTMENT_ADMIN, Role.SPACE_CAPTAIN, Role.BOOKINGS_MANAGER]
  let manageAll = true
  if (role === Role.BOOKINGS_MANAGER && bookingManagerSetting?.manage_all) {
    manageAll = false
  }
  return allowedToShowRoles.some((allowed) => role === allowed) && manageAll
}

const hasLocationAdminRights = (role: Role) =>
  [Role.LOCATION_ADMIN, Role.DEPARTMENT_ADMIN, Role.SPACE_CAPTAIN].includes(role)

const resetEmployeePreferredLocations = async (employeeId: string) => {
  const locations = await api.company.geoLocations()

  try {
    await api.employee.preferredLocations.update(
      employeeId,
      locations.map((location) => location.id)
    )

    Toaster.success({
      message: 'HR Locations Saved.',
    })
  } catch (error: any) {
    Toaster.fail({ error })
  }
}

function RoleSection({
  employee,
  updateRole,
  updateManageAllDeptAndEmp,
  bookingManagerSetting,
}: EmployeeProps) {
  const [empIds, setEmpIds] = useState([] as string[])
  const [deptIds, setDeptIds] = useState([] as string[])

  return (
    <>
      <H4>SpaceIQ Role</H4>
      <RoleSelect
        disabled={!updateRole}
        value={employee.role}
        onChange={(role) => {
          if (!role || !updateRole) return
          updateRole(role)

          if (hasLocationAdminRights(role) && role === Role.BOOKINGS_MANAGER) {
            resetEmployeePreferredLocations(employee.id)
          }
        }}
      />
      {employee.role === Role.BOOKINGS_MANAGER ? (
        <Checkbox
          disabled={!updateRole}
          checked={bookingManagerSetting !== null ? bookingManagerSetting['manage_all'] : false}
          onChange={({ currentTarget: { checked } }: React.SyntheticEvent<HTMLInputElement>) => {
            updateManageAllDeptAndEmp && updateManageAllDeptAndEmp(checked)
          }}
          style={{ marginTop: 16 }}
          label="Manage All Departments & Employees"
        />
      ) : null}

      <ManagedLocationsList
        employee={employee}
        readOnly={!updateRole}
        employeeId={employee.id}
        role={employee.role}
      />

      <ManagedDepartmentsList
        readOnly={!updateRole}
        bookingManagerSetting={bookingManagerSetting}
        employeeId={employee.id}
        role={employee.role}
        updateDeptArray={(deptIds: string[]) => setDeptIds(deptIds)}
        empIds={empIds}
        employee={employee}
      />

      {employee?.role === Role.BOOKINGS_MANAGER && !bookingManagerSetting?.manage_all && (
        <ManagedEmployeesList
          readOnly={!updateRole}
          bookingManagerSetting={bookingManagerSetting}
          employeeId={employee.id}
          role={employee.role}
          deptIds={deptIds}
          updateEmployeeList={(empIds: string[]) => setEmpIds(empIds)}
        />
      )}
    </>
  )
}

function NoteSection({ note, updateNote, id }: NoteProps) {
  const [value, setValue] = useState(note)

  useEffect(() => {
    setValue(note)
  }, [note])

  return (
    <div style={{ fontSize: 15 }}>
      <H4 style={{ display: 'inline-flex' }}>Note</H4>
      <EditableText
        multiline
        minLines={2}
        maxLines={1000}
        placeholder="Add a note..."
        disabled={!updateNote}
        value={value}
        onChange={setValue}
        onConfirm={
          updateNote
            ? () => {
                if (value !== note) {
                  updateNote(value)
                  api.employee
                    .update({ id, note: value })
                    .then(() =>
                      Toaster.success({
                        message: 'Note updated',
                      })
                    )
                    .catch((error: ApolloError) =>
                      Toaster.fail({
                        error,
                      })
                    )
                }
              }
            : () => undefined
        }
      />
    </div>
  )
}

function EmployeeInfo({ employee, navigate }: EmployeeProps) {
  const employeeInfo: IEmployeeInfoData[] = [
    { label: 'Location', value: employee.geoLocation.location },
    {
      label: 'Department/Team',
      value: `${employee.dept.name}
    ${employee.team ? `/${employee.team.name}` : ''}`,
    },
    {
      label: 'Phone',
      value: employee.phone || '-',
    },
    {
      label: 'Manager',
      value: employee.manager ? (
        <div style={{ marginLeft: -10 }}>
          <EmployeeWithDetailsPopover employee={employee.manager} buttonProps={{ minimal: true }} />
        </div>
      ) : (
        '-'
      ),
    },
    {
      label: 'Title',
      value: employee.title || '-',
    },
    {
      label: 'Start Date',
      value: employee.startDate ? DateUtil(employee.startDate).format('MMM dd, yyyy') : '-',
    },
    {
      label: 'Badge',
      value: employee.badge || '-',
    },
    {
      label: 'End date',
      value: employee.endDate ? DateUtil(employee.endDate).format('MMM dd, yyyy') : '-',
    },
    {
      label: 'Type',
      value: employee.employmentType,
    },
    {
      label: 'Cost Center',
      value: `${employee.costCenter ? `${employee.costCenter.name}` : '-'}`,
    },
  ]

  employee.externalId && employeeInfo.push({ label: 'External ID', value: employee.externalId })
  employee.slackHandle && employeeInfo.push({ label: 'Slack Handle', value: employee.slackHandle })
  employee.extAttributes &&
    Object.entries<string | { value: string }>(JSON.parse(employee.extAttributes)).forEach(
      ([k, v]) => {
        if (typeof v === 'string') {
          employeeInfo.push({
            label: k,
            value: v,
          })
        }
      }
    )

  const paired = employeeInfo.reduce(
    (result: [IEmployeeInfoData, IEmployeeInfoData][], current, i) => {
      if (i % 2 === 0) {
        /* eslint-disable @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        result.push([current])
        return result
      } else {
        result[Math.floor(i / 2)].push(current)
        return result
      }
    },
    []
  )

  return (
    <>
      <H4>Employee info</H4>
      <table className={css.table}>
        <tbody>
          {paired.map((pair, i) => (
            <tr key={i}>
              {pair.map(({ label, value }: IEmployeeInfoData) => (
                <>
                  <th className={css.th}>{label}</th>
                  <td
                    className={css.td}
                    {...{
                      [`data-${label}`]: typeof value === 'string' ? value.toLowerCase() : '',
                    }}>
                    <div className={css.textWrap}>{value}</div>
                  </td>
                </>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

interface IEmployeeInfoData {
  label: string
  value: string | React.ReactNode
}

function DetailsSkeleton() {
  const placeholder = <span className={Classes.SKELETON}>Placeholder text</span>
  return (
    <div className={Classes.DIALOG_BODY}>
      <section className={css.section}>
        <Flex align="center" justify="space-between">
          <div>
            <div style={{ fontSize: 32 }}>{placeholder}</div>
            <div
              style={{
                fontSize: 18,
                color: 'var(--grey1)',
                marginTop: 20,
                marginBottom: 5,
              }}>
              {placeholder}
            </div>
          </div>
          <Avatar skeleton size={Avatar.Size.LG} />
        </Flex>
      </section>
      <section className={css.section} style={{ marginTop: 14 }}>
        <H4>Employee info</H4>
        <table className={css.table}>
          <tbody>
            <tr>
              <th className={css.th}>Location</th>
              <td className={css.td}>{placeholder}</td>
              <th className={css.th}>Department/Team</th>
              <td className={css.td}>{placeholder}</td>
            </tr>
            <tr>
              <th className={css.th}>Phone</th>
              <td className={css.td}>{placeholder}</td>
              <th className={css.th}>Manager</th>
              <td className={css.td}>{placeholder}</td>
            </tr>
            <tr>
              <th className={css.th}>Title</th>
              <td className={css.td}>{placeholder}</td>
              <th className={css.th}>Start Date</th>
              <td className={css.td}>{placeholder}</td>
            </tr>
            <tr>
              <th className={css.th}>Badge</th>
              <td className={css.td}>{placeholder}</td>
              <th className={css.th}>End date</th>
              <td className={css.td}>{placeholder}</td>
            </tr>
            <tr>
              <th className={css.th}>Type</th>
              <td className={css.td}>{placeholder}</td>
            </tr>
            <tr>
              <th className={css.th}>Cost Center</th>
              <td className={css.td}>{placeholder}</td>
            </tr>
          </tbody>
        </table>
      </section>
      <section className={css.section} style={{ marginTop: 14 }}>
        <H4>SpaceIQ Role</H4>
        <div>{placeholder}</div>
      </section>
      <section className={css.section} style={{ marginTop: 14 }}>
        <H4>Note</H4>
        <div>{placeholder}</div>
      </section>
    </div>
  )
}
